import React from 'react';
import { getLocalTimeZone, parseDate } from '@internationalized/date';
import { useSales } from '@/pages/sales/SalesProvider';
import { useTimeFrame } from '@/utils/useTimeFrame';
import {
    type PeriodRange,
    type PeriodShortcut,
    detectShortcut,
    getRangeForShortcut,
} from './periodControlMeta';

/**
 * Drives the sales-page date-range filter. Owns the two-way sync between the
 * `?start=`/`?end=` URL params, the page-level `SalesProvider` range, and the
 * HeroUI `DateRangePicker`'s `CalendarDate` shape, plus the shortcut-segment
 * state. Render lives in `PeriodControlView`.
 */
export const usePeriodControl = () => {
    const tz = React.useMemo(() => getLocalTimeZone(), []);

    const { dateRange, setDateRange } = useSales();
    const {
        startDate: urlStart,
        endDate: urlEnd,
        setTimeFrame,
    } = useTimeFrame();

    // URL → provider sync. Runs on mount and whenever the `?start=` /
    // `?end=` params change externally (e.g. browser back/forward).
    // The diff guard is what keeps this from ping-ponging with the
    // write-to-URL side-effect below: once both sides agree, the
    // effect short-circuits and no further updates fire.
    React.useEffect(() => {
        if (urlStart === null || urlEnd === null) {
            return;
        }
        if (urlStart === dateRange.start && urlEnd === dateRange.end) {
            return;
        }
        setDateRange({ start: urlStart, end: urlEnd });
    }, [urlStart, urlEnd, dateRange.start, dateRange.end, setDateRange]);

    // Adapt the provider's ISO strings into the CalendarDate range
    // that the HeroUI DateRangePicker speaks. `parseDate` accepts the
    // exact `YYYY-MM-DD` shape we store in the context.
    const range = React.useMemo<PeriodRange | null>(
        () => ({
            start: parseDate(dateRange.start),
            end: parseDate(dateRange.end),
        }),
        [dateRange.end, dateRange.start],
    );

    const [isPickerOpen, setIsPickerOpen] = React.useState(false);

    const selectedShortcut = React.useMemo(
        () => detectShortcut(range, tz),
        [range, tz],
    );

    const onRangeChange = React.useCallback(
        (next: PeriodRange | null) => {
            if (next === null) {
                return;
            }
            const isoRange = {
                start: next.start.toString(),
                end: next.end.toString(),
            };
            // Push to both the provider AND the URL. Order doesn't
            // matter: the URL-sync effect above checks `urlX === dateRange.X`
            // before firing a redundant setDateRange, so the round-trip
            // from URL change → effect → provider is a no-op when both
            // sides already agree.
            setDateRange(isoRange);
            setTimeFrame(isoRange);
        },
        [setDateRange, setTimeFrame],
    );

    /** The shared picker's shape: two ISO `YYYY-MM-DD` strings. */
    const onDateRangeChange = React.useCallback(
        (fromDate: string, toDate: string) => {
            onRangeChange({
                start: parseDate(fromDate),
                end: parseDate(toDate),
            });
        },
        [onRangeChange],
    );

    const onShortcutChange = React.useCallback(
        (key: React.Key) => {
            const next = key as PeriodShortcut;
            if (next === 'custom') {
                // Clicking Custom on top of a preset opens the picker so the
                // user can dial in a bespoke range. If we're already on
                // Custom, leave the existing range alone.
                if (selectedShortcut !== 'custom') {
                    setIsPickerOpen(true);
                }
                return;
            }
            const presetRange = getRangeForShortcut(next, tz);
            if (presetRange !== null) {
                onRangeChange(presetRange);
            }
        },
        [selectedShortcut, tz, onRangeChange],
    );

    return {
        // ISO strings, because the View renders the shared `DateRangeField`
        // rather than a second copy of the picker tree (BOS-128). `range` stays
        // in `CalendarDate` for `detectShortcut`, which compares against the
        // preset ranges.
        fromDate: dateRange.start,
        toDate: dateRange.end,
        selectedShortcut,
        isPickerOpen,
        onPickerOpenChange: setIsPickerOpen,
        onShortcutChange,
        onDateRangeChange,
    };
};
